/**
 * The rules a skill's own statuses obey, written once over a neutral model so the skill language's checker and the
 * canonical validator refuse exactly the same programs with exactly the same codes.
 *
 * A skill status is local to the skill and to one cast, and is deliberately simple: it is created by a stage, lasts a
 * time (or until a stage settles, or until the cast's authored work is done), may stack and carry a timer, may bind
 * other effects to its lifetime (`while`), may release a stage when it is applied, ticks or ends (a hook), and may be
 * swept (a stage released once at each carrier). It never converts and is never read by another skill.
 */
import { elementPackage } from '../sim/element-package.js';

/** PROVISIONAL: complexity bound; a skill that needs more statuses than this is doing too many things at once. */
export const MAX_SKILL_STATUSES = 3;

/** Bounded for display; the name is the player's own word for the status. */
export const SKILL_STATUS_NAME_PATTERN = /^[a-z][a-z0-9_]{0,11}$/;

/** The id a skill status carries on an actor. The cast is the `executionId` on the status, not part of the id. */
export function skillStatusDefinitionId(skillId: string, name: string): string {
  return `skill:${skillId}:${name}`;
}

/** The name a skill status's id ends with (a name holds no `:`); undefined for any other definition. */
export function skillStatusNameOf(definitionId: string): string | undefined {
  if (!definitionId.startsWith('skill:')) return undefined;
  const name = definitionId.slice(definitionId.lastIndexOf(':') + 1);
  return SKILL_STATUS_NAME_PATTERN.test(name) ? name : undefined;
}

export type SkillStatusRuleCode =
  | 'SKILL_STATUS_UNKNOWN'
  | 'SKILL_STATUS_CONFLICT'
  | 'SKILL_STATUS_TOO_MANY'
  | 'SKILL_STATUS_NAME_INVALID'
  | 'SKILL_STATUS_BOUND_UNREACHABLE'
  | 'SKILL_STATUS_TICK_INVALID'
  | 'SKILL_STATUS_NOT_STACKED'
  | 'SKILL_STATUS_LIFETIME_CYCLE'
  | 'SKILL_STATUS_HOOK_DEFAULT_LIFETIME'
  | 'SKILL_STATUS_SELF_HOOK'
  | typeof ELEMENT_STATUS_NOT_HOOKABLE
  | 'COMPOSITE_STAGE_TIMING_UNKNOWN_REFERENCE'
  | 'BAD_EFFECT_DURATION'
  | 'BAD_EFFECT_VALUE';

export const SKILL_STATUS_RULE_CODES: readonly SkillStatusRuleCode[] = Object.freeze([
  'SKILL_STATUS_UNKNOWN', 'SKILL_STATUS_CONFLICT', 'SKILL_STATUS_TOO_MANY', 'SKILL_STATUS_NAME_INVALID', 'SKILL_STATUS_BOUND_UNREACHABLE',
  'SKILL_STATUS_TICK_INVALID', 'SKILL_STATUS_NOT_STACKED', 'SKILL_STATUS_LIFETIME_CYCLE', 'SKILL_STATUS_HOOK_DEFAULT_LIFETIME',
  'SKILL_STATUS_SELF_HOOK',
]);

/**
 * An element status is defined once per element as engine data, so it is never what an effect binds to (`while`), what a
 * hook answers or what a sweep scales by: a program that names one there is refused, whether or not it applies it.
 */
export const ELEMENT_STATUS_NOT_HOOKABLE = 'ELEMENT_STATUS_NOT_HOOKABLE' as const;

/** The element statuses `beta-elemental-damage-status-contract.md` §2C names: engine data, never a skill's own status. */
export const ELEMENT_STATUS_NAMES: readonly string[] = Object.freeze(['frostbite', 'freeze', 'conductive', 'shock', 'paralysis', 'paralysis_lockout', 'burn', 'poison', 'acid']);

/** A name of an element status: one the contract names, or one the registry holds. */
export function isElementStatusName(name: string): boolean {
  return ELEMENT_STATUS_NAMES.includes(name) || elementPackage(name) !== undefined;
}

/** The refusal for an element status whose stage deals none of the element it is left by. */
export const ELEMENT_STATUS_ELEMENT_MISMATCH = 'ELEMENT_STATUS_ELEMENT_MISMATCH' as const;

/**
 * The one rule that an element status is what a hit of its element leaves behind: its stage must deal damage or a dot of that
 * element. The canonical validator and the language checker each gather the elements `dealt` from their own form of the
 * stage and ask this; `undefined` when the status fits (or names no registered element status, which another rule refuses).
 */
export function elementStatusMismatch(name: string, dealt: ReadonlySet<string>, stage: string): { readonly code: typeof ELEMENT_STATUS_ELEMENT_MISMATCH; readonly message: string } | undefined {
  const spec = elementPackage(name);
  if (spec === undefined || dealt.has(spec.element)) return undefined;
  return { code: ELEMENT_STATUS_ELEMENT_MISMATCH, message: `${name} is left by ${spec.element} damage, and stage ${stage} deals none` };
}

export interface StatusDef {
  readonly name: string;
  readonly durationS?: number;
  /** The key of the stage whose settling ends the status. */
  readonly untilStage?: string;
  readonly stacked: boolean;
  readonly stackMax?: number;
  readonly tickS?: number;
}

export type StageStatusTiming =
  /** Released by the cast's own clock. */
  | { readonly kind: 'plain' }
  /** Released by an event of an earlier stage; `sweep` when it is released once at each carrier of `status`. */
  | { readonly kind: 'event'; readonly of: string; readonly sweep?: { readonly status: string; readonly strength: 'ONE' | 'STACKS' } }
  | { readonly kind: 'hook'; readonly status: string; readonly event: 'APPLY' | 'TICK' | 'END' };

export interface StatusStage {
  readonly key: string;
  /** The statuses its effects create, in effect order. */
  readonly creates: readonly string[];
  /** The statuses its effects are bound to (`while`). */
  readonly whiles: readonly string[];
  readonly timing: StageStatusTiming;
}

export interface StatusRuleRefusal {
  readonly code: SkillStatusRuleCode;
  readonly message: string;
  readonly stage?: string;
}

const hasDefaultLifetime = (def: StatusDef): boolean => def.durationS === undefined && def.untilStage === undefined;

/**
 * Stages whose release is a consequence of a hook: a hook stage, and any stage released by an event of one. They are
 * the follow-up of a status, not the cast's authored work.
 */
export function hookDerivedStages(stages: readonly StatusStage[]): ReadonlySet<string> {
  const derived = new Set<string>();
  for (const stage of stages) {
    if (stage.timing.kind === 'hook' || (stage.timing.kind === 'event' && derived.has(stage.timing.of))) derived.add(stage.key);
  }
  return derived;
}

/**
 * How many generations below the cast's own attacks each stage sits: an event stage is one below its reference, a hook
 * stage one below the deepest stage that creates the status it hooks. A chain that feeds itself (a hook that creates the
 * status another hook releases it from) has no depth; it is reported as `Infinity`.
 */
export function statusStageDepths(stages: readonly StatusStage[], ceiling: number): ReadonlyMap<string, number> {
  const depth = new Map<string, number>(stages.map((stage) => [stage.key, 0] as const));
  const creators = new Map<string, string[]>();
  for (const stage of stages) for (const name of stage.creates) creators.set(name, [...(creators.get(name) ?? []), stage.key]);
  const depthOf = (stage: StatusStage): number => {
    if (stage.timing.kind === 'plain') return 0;
    if (stage.timing.kind === 'event') return (depth.get(stage.timing.of) ?? 0) + 1;
    return Math.max(0, ...(creators.get(stage.timing.status) ?? []).map((key) => depth.get(key) ?? 0)) + 1;
  };
  // Each pass settles one more level of the chain; more passes than the ceiling allows means the chain feeds itself.
  for (let pass = 0; pass <= stages.length + ceiling + 1; pass += 1) {
    let changed = false;
    for (const stage of stages) {
      const next = depthOf(stage);
      if (next !== depth.get(stage.key)) { depth.set(stage.key, next); changed = true; }
    }
    if (!changed) return depth;
  }
  for (const stage of stages) if (stage.timing.kind !== 'plain') depth.set(stage.key, Infinity);
  return depth;
}

/**
 * A cycle in the waits-for graph over milestones, or `undefined`. Nodes are `settled(stage)`, `ended(status)` and
 * `authored` (the cast's authored work is done). Creating a status is not a wait.
 */
export function statusWaitCycle(defs: readonly StatusDef[], stages: readonly StatusStage[]): readonly string[] | undefined {
  const edges = new Map<string, Set<string>>();
  const wait = (from: string, to: string): void => { edges.set(from, (edges.get(from) ?? new Set()).add(to)); };
  const derived = hookDerivedStages(stages);
  const byName = new Map(defs.map((def) => [def.name, def] as const));
  for (const def of defs) {
    if (def.untilStage !== undefined) wait(`ended(${def.name})`, `settled(${def.untilStage})`);
    if (hasDefaultLifetime(def)) wait(`ended(${def.name})`, 'authored');
  }
  for (const stage of stages) {
    if (stage.timing.kind === 'hook' && byName.has(stage.timing.status) && stage.timing.event !== 'APPLY') wait(`settled(${stage.key})`, `ended(${stage.timing.status})`);
    if (stage.timing.kind === 'event') wait(`settled(${stage.key})`, `settled(${stage.timing.of})`);
    if (!derived.has(stage.key)) wait('authored', `settled(${stage.key})`);
  }
  const state = new Map<string, 'open' | 'done'>();
  const path: string[] = [];
  const visit = (node: string): readonly string[] | undefined => {
    if (state.get(node) === 'done') return undefined;
    if (state.get(node) === 'open') return [...path.slice(path.indexOf(node)), node];
    state.set(node, 'open');
    path.push(node);
    for (const next of edges.get(node) ?? []) {
      const found = visit(next);
      if (found !== undefined) return found;
    }
    path.pop();
    state.set(node, 'done');
    return undefined;
  };
  for (const node of [...edges.keys()].sort()) {
    const found = visit(node);
    if (found !== undefined) return found;
  }
  return undefined;
}

/** Every rule that is about statuses and nothing else; the callers answer for the rest of the program. */
export function checkStatusRules(defs: readonly StatusDef[], stages: readonly StatusStage[], limits: { readonly minTickS: number }): readonly StatusRuleRefusal[] {
  const refusals: StatusRuleRefusal[] = [];
  const refuse = (code: SkillStatusRuleCode, message: string, stage?: string): void => { refusals.push({ code, message, ...(stage === undefined ? {} : { stage }) }); };
  const byName = new Map<string, StatusDef>();
  for (const def of defs) {
    if (!SKILL_STATUS_NAME_PATTERN.test(def.name)) refuse('SKILL_STATUS_NAME_INVALID', `status name ${JSON.stringify(def.name)} must be a lowercase letter then up to 11 lowercase letters, digits or underscores`);
    if (byName.has(def.name)) refuse('SKILL_STATUS_CONFLICT', `status ${def.name} is defined twice`);
    byName.set(def.name, def);
  }
  if (byName.size > MAX_SKILL_STATUSES) refuse('SKILL_STATUS_TOO_MANY', `a skill names at most ${MAX_SKILL_STATUSES} statuses, this one names ${byName.size}`);

  const stageKeys = new Set(stages.map((stage) => stage.key));
  const createdAnywhere = new Set(stages.flatMap((stage) => stage.creates));
  const unknown = (name: string, use: string, stage?: string): void => {
    if (isElementStatusName(name)) refuse(ELEMENT_STATUS_NOT_HOOKABLE, `${use} names ${name}, an element status: it is engine data, never what a skill binds to, hooks or sweeps`, stage);
    else if (!byName.has(name)) refuse('SKILL_STATUS_UNKNOWN', `${use} names status ${name}, which this skill does not define`, stage);
    else if (!createdAnywhere.has(name)) refuse('SKILL_STATUS_UNKNOWN', `${use} names status ${name}, which no stage creates`, stage);
  };

  for (const def of byName.values()) {
    if (def.durationS !== undefined && def.untilStage !== undefined) refuse('SKILL_STATUS_CONFLICT', `status ${def.name} lasts either seconds or until a stage ends, not both`);
    if (def.durationS !== undefined && !(Number.isFinite(def.durationS) && def.durationS > 0)) refuse('BAD_EFFECT_DURATION', `status ${def.name} lasts a duration above zero`);
    if (def.untilStage !== undefined && !stageKeys.has(def.untilStage)) refuse('COMPOSITE_STAGE_TIMING_UNKNOWN_REFERENCE', `status ${def.name} lasts until ${def.untilStage}, which names no stage`);
    if (def.stackMax !== undefined && !(Number.isInteger(def.stackMax) && def.stackMax >= 1)) refuse('BAD_EFFECT_VALUE', `status ${def.name} stacks to an integer of at least 1`);
    if (def.tickS !== undefined && !(Number.isFinite(def.tickS) && def.tickS >= limits.minTickS)) refuse('SKILL_STATUS_TICK_INVALID', `status ${def.name} ticks at least every ${limits.minTickS}s`);
  }

  const order = new Map(stages.map((stage, index) => [stage.key, index] as const));
  const creatorOrders = new Map<string, number[]>();
  for (const stage of stages) for (const name of stage.creates) creatorOrders.set(name, [...(creatorOrders.get(name) ?? []), order.get(stage.key)!]);
  const derived = hookDerivedStages(stages);

  for (const stage of stages) {
    const here = order.get(stage.key)!;
    for (const name of stage.creates) {
      if (!byName.has(name)) refuse('SKILL_STATUS_UNKNOWN', `${stage.key} creates status ${name}, which this skill does not define`, stage.key);
    }
    for (const name of stage.whiles) {
      unknown(name, `${stage.key} binds an effect to it (while)`, stage.key);
      // A status effect of the stage is applied before the others, so the stage's own creation counts.
      if (byName.has(name) && createdAnywhere.has(name) && !(creatorOrders.get(name) ?? []).some((creator) => creator <= here)) {
        refuse('SKILL_STATUS_BOUND_UNREACHABLE', `${stage.key} binds an effect to status ${name}, but neither it nor an earlier stage creates it`, stage.key);
      }
    }
    const { timing } = stage;
    if (timing.kind === 'hook') {
      unknown(timing.status, `the hook ${stage.key}`, stage.key);
      if (stage.creates.includes(timing.status)) refuse('SKILL_STATUS_SELF_HOOK', `${stage.key} is released by status ${timing.status} and creates it`, stage.key);
      const hooked = byName.get(timing.status);
      if (timing.event === 'TICK' && hooked !== undefined && hooked.tickS === undefined) refuse('SKILL_STATUS_TICK_INVALID', `${stage.key} hooks the tick of status ${timing.status}, which has no tickS`, stage.key);
    }
    if (timing.kind === 'event' && timing.sweep !== undefined) {
      unknown(timing.sweep.status, `the sweep ${stage.key}`, stage.key);
      const swept = byName.get(timing.sweep.status);
      if (timing.sweep.strength === 'STACKS' && swept !== undefined && !swept.stacked) refuse('SKILL_STATUS_NOT_STACKED', `${stage.key} scales by the stacks of status ${timing.sweep.status}, which does not stack`, stage.key);
    }
    if (derived.has(stage.key)) {
      for (const name of stage.creates) {
        const def = byName.get(name);
        if (def !== undefined && hasDefaultLifetime(def)) {
          refuse('SKILL_STATUS_HOOK_DEFAULT_LIFETIME', `${stage.key} is released by a hook and creates status ${name}, whose default lifetime (the cast's authored work) would already have passed`, stage.key);
        }
      }
    }
  }

  const cycle = statusWaitCycle([...byName.values()], stages);
  if (cycle !== undefined) refuse('SKILL_STATUS_LIFETIME_CYCLE', `statuses and stages wait on each other: ${cycle.join(' → ')}`);
  return refusals;
}
